import { apiClient } from './api';
import { uploadMessagingAttachmentAsset } from './assetUploadService';

const BASE = '/api/messaging';

export function getMessagingErrorMessage(error, fallback = 'Unable to complete that messaging request right now.') {
  return String(
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  ).trim() || fallback;
}

function normalizeActor(actor = null) {
  if (!actor) {
    return { type: 'SHOPPER', businessId: null };
  }

  if (actor.type !== 'SELLER') {
    return { type: 'SHOPPER', businessId: null };
  }

  if (!actor.businessId) {
    throw new Error('Active business selection is required for seller messaging requests.');
  }

  return { type: 'SELLER', businessId: Number(actor.businessId) };
}

function getActorBasePath(actor = null) {
  const resolvedActor = normalizeActor(actor);
  if (resolvedActor.type === 'SELLER') {
    return `${BASE}/businesses/${resolvedActor.businessId}`;
  }
  return BASE;
}

function buildConversationPath(conversationId, actor = null) {
  return `${getActorBasePath(actor)}/conversations/${conversationId}`;
}

/**
 * Start or get a conversation with a business.
 */
export const startConversation = async (businessId) => {
  const { data } = await apiClient.post(`${BASE}/conversations`, { businessId });
  return data;
};

/**
 * List all conversations for the current shopper.
 */
export const listConversations = async (actor = null, params = {}) => {
  const { data } = await apiClient.get(`${getActorBasePath(actor)}/conversations`, { params });
  return Array.isArray(data) ? data : [];
};

/**
 * Get messages for a conversation.
 * @param {number} conversationId
 * @param {{ before?: number, limit?: number }} params
 */
export const getMessages = async (conversationId, params = {}, actor = null) => {
  const { data } = await apiClient.get(`${buildConversationPath(conversationId, actor)}/messages`, { params });
  return Array.isArray(data) ? data : [];
};

/**
 * Send a text message (with optional file attachment).
 * @param {number} conversationId
 * @param {string} text
 * @param {File|null} file
 */
export const sendMessage = async (conversationId, text, file = null, actor = null) => {
  const normalizedConversationId = Number(conversationId);
  if (!Number.isInteger(normalizedConversationId) || normalizedConversationId <= 0) {
    throw new Error('A valid conversation is required before sending a message.');
  }

  const url = `${buildConversationPath(normalizedConversationId, actor)}/messages`;
  const normalizedText = typeof text === 'string' ? text : '';
  let attachmentAssetId = null;

  if (file) {
    const uploadResult = await uploadMessagingAttachmentAsset(
      file,
      { conversationId: normalizedConversationId },
      actor?.type === 'SELLER' ? { headers: { 'X-Portal': 'business' } } : {}
    );
    attachmentAssetId = uploadResult?.assetId || uploadResult?.data?.assetId || null;
    if (!attachmentAssetId) {
      throw new Error('Request failed: Messaging attachment upload did not return an asset id.');
    }
  }

  const payload = {};
  if (normalizedText) payload.text = normalizedText;
  if (attachmentAssetId) payload.attachmentAssetId = attachmentAssetId;

  const { data } = await apiClient.post(url, payload);
  return data;
};

/**
 * Mark a conversation as read.
 */
export const markConversationRead = async (conversationId, actor = null) => {
  const { data } = await apiClient.patch(`${buildConversationPath(conversationId, actor)}/read`);
  return data;
};

/**
 * Delete a message (only your own).
 */
export const deleteMessage = async (conversationId, messageId, actor = null) => {
  const { data } = await apiClient.delete(`${buildConversationPath(conversationId, actor)}/messages/${messageId}`);
  return data;
};

/**
 * Get total unread message count.
 */
export const getUnreadCount = async (actor = null) => {
  const { data } = await apiClient.get(`${getActorBasePath(actor)}/unread-count`);
  return data.count || 0;
};

export const messagingActorUtils = {
  normalizeActor,
};
